import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import SettingsStore from "@/stores/SettingsStore";
import { useTranslation } from "react-i18next";
import { ControlWrapper } from "@/components/ui/control-wrapper";
import { ImageIcon, MessageSquare, Palette, Type } from "lucide-react";

function isValidHex(value: string): boolean {
  return /^#[0-9a-fA-F]{6}$/.test(value);
}

function normalizeHex(value: string): string {
  if (!value) return "";
  const trimmed = value.trim();
  if (isValidHex(trimmed)) return trimmed.toUpperCase();
  if (/^[0-9a-fA-F]{6}$/.test(trimmed)) return `#${trimmed.toUpperCase()}`;
  return trimmed;
}

function ColorInput({ label, value, onChange, id }: { label: string; value: string; onChange: (v: string) => void; id: string }) {
  const displayValue = value || "";
  const pickerValue = isValidHex(value) ? value : "#000000";

  return (
    <div className="space-y-2">
      <Label htmlFor={id}>{label}</Label>
      <div className="flex items-center gap-3">
        <input
          type="color"
          id={id}
          value={pickerValue}
          onChange={(e) => onChange(e.target.value.toUpperCase())}
          className="w-10 h-10 rounded border cursor-pointer"
        />
        <Input
          value={displayValue}
          onChange={(e) => onChange(normalizeHex(e.target.value))}
          onBlur={(e) => {
            const normalized = normalizeHex(e.target.value);
            if (normalized !== displayValue) onChange(normalized);
          }}
          placeholder="#4A90D9"
          className="flex-1 font-mono"
        />
        {value && isValidHex(value) && (
          <div
            className="w-10 h-10 rounded border shrink-0"
            style={{ backgroundColor: value }}
          />
        )}
      </div>
    </div>
  );
}

export function BrandingSettings() {
  const { t } = useTranslation();
  const isLoading = SettingsStore(state => state.isLoading);

  const brandingLogoUrl = SettingsStore(state => state.brandingLogoUrl);
  const brandingPlayLogoUrl = SettingsStore(state => state.brandingPlayLogoUrl);
  const brandingPlayLogoHoverUrl = SettingsStore(state => state.brandingPlayLogoHoverUrl);
  const brandingFaviconUrl = SettingsStore(state => state.brandingFaviconUrl);
  const brandingTitle = SettingsStore(state => state.brandingTitle);
  const brandingPrimaryColor = SettingsStore(state => state.brandingPrimaryColor);
  const brandingCtaColor = SettingsStore(state => state.brandingCtaColor);
  const brandingAccentColor = SettingsStore(state => state.brandingAccentColor);
  const brandingUserMsgBg = SettingsStore(state => state.brandingUserMsgBg);
  const brandingBotMsgBg = SettingsStore(state => state.brandingBotMsgBg);
  const brandingUserMsgText = SettingsStore(state => state.brandingUserMsgText);
  const brandingBotMsgText = SettingsStore(state => state.brandingBotMsgText);

  const setBrandingLogoUrl = SettingsStore(state => state.setBrandingLogoUrl);
  const setBrandingPlayLogoUrl = SettingsStore(state => state.setBrandingPlayLogoUrl);
  const setBrandingPlayLogoHoverUrl = SettingsStore(state => state.setBrandingPlayLogoHoverUrl);
  const setBrandingFaviconUrl = SettingsStore(state => state.setBrandingFaviconUrl);
  const setBrandingTitle = SettingsStore(state => state.setBrandingTitle);
  const setBrandingPrimaryColor = SettingsStore(state => state.setBrandingPrimaryColor);
  const setBrandingCtaColor = SettingsStore(state => state.setBrandingCtaColor);
  const setBrandingAccentColor = SettingsStore(state => state.setBrandingAccentColor);
  const setBrandingUserMsgBg = SettingsStore(state => state.setBrandingUserMsgBg);
  const setBrandingBotMsgBg = SettingsStore(state => state.setBrandingBotMsgBg);
  const setBrandingUserMsgText = SettingsStore(state => state.setBrandingUserMsgText);
  const setBrandingBotMsgText = SettingsStore(state => state.setBrandingBotMsgText);

  return (
    <div className="space-y-6">
      {/* Logos */}
      <ControlWrapper>
        <div className="flex items-center gap-2 mb-4">
          <ImageIcon className="h-5 w-5 text-muted-foreground" />
          <h3 className="font-semibold">{t("branding.logos")}</h3>
        </div>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="branding-logo-url">{t("branding.logoUrl")}</Label>
            <Input
              id="branding-logo-url"
              value={brandingLogoUrl ?? ""}
              onChange={(e) => setBrandingLogoUrl(e.target.value)}
              placeholder={t("branding.logoUrlPlaceholder")}
              disabled={isLoading}
            />
            {brandingLogoUrl && (
              <div className="mt-2 p-3 border rounded-md bg-muted/50">
                <img src={brandingLogoUrl} alt="Logo Preview" className="max-h-16 object-contain" />
              </div>
            )}
          </div>
          <div className="space-y-2">
            <Label htmlFor="branding-play-logo-url">{t("branding.playLogoUrl")}</Label>
            <Input
              id="branding-play-logo-url"
              value={brandingPlayLogoUrl ?? ""}
              onChange={(e) => setBrandingPlayLogoUrl(e.target.value)}
              placeholder={t("branding.playLogoUrlPlaceholder")}
              disabled={isLoading}
            />
            {brandingPlayLogoUrl && (
              <div className="mt-2 p-3 border rounded-md bg-muted/50 flex items-center justify-center">
                <img src={brandingPlayLogoUrl} alt="Play Logo Preview" className="max-h-16 object-contain" />
              </div>
            )}
          </div>
          <div className="space-y-2">
            <Label htmlFor="branding-play-logo-hover-url">{t("branding.playLogoHoverUrl")}</Label>
            <Input
              id="branding-play-logo-hover-url"
              value={brandingPlayLogoHoverUrl ?? ""}
              onChange={(e) => setBrandingPlayLogoHoverUrl(e.target.value)}
              placeholder={t("branding.playLogoHoverUrlPlaceholder")}
              disabled={isLoading}
            />
            {brandingPlayLogoHoverUrl && (
              <div className="mt-2 p-3 border rounded-md bg-muted/50 flex items-center justify-center">
                <img src={brandingPlayLogoHoverUrl} alt="Play Logo Hover Preview" className="max-h-16 object-contain" />
              </div>
            )}
          </div>
          <div className="space-y-2">
            <Label htmlFor="branding-favicon-url">{t("branding.faviconUrl")}</Label>
            <Input
              id="branding-favicon-url"
              value={brandingFaviconUrl ?? ""}
              onChange={(e) => setBrandingFaviconUrl(e.target.value)}
              placeholder={t("branding.faviconUrlPlaceholder")}
              disabled={isLoading}
            />
            {brandingFaviconUrl && (
              <div className="mt-2 p-3 border rounded-md bg-muted/50 flex items-center gap-3">
                <img src={brandingFaviconUrl} alt="Favicon Preview" className="w-8 h-8 object-contain" />
                <span className="text-xs text-muted-foreground">32x32 px</span>
              </div>
            )}
          </div>
        </div>
      </ControlWrapper>

      {/* Titel */}
      <ControlWrapper>
        <div className="flex items-center gap-2 mb-4">
          <Type className="h-5 w-5 text-muted-foreground" />
          <h3 className="font-semibold">{t("branding.titleSection")}</h3>
        </div>
        <div className="space-y-2">
          <Label htmlFor="branding-title">{t("branding.siteTitle")}</Label>
          <Input
            id="branding-title"
            value={brandingTitle ?? ""}
            onChange={(e) => setBrandingTitle(e.target.value)}
            placeholder={t("branding.siteTitlePlaceholder")}
            disabled={isLoading}
          />
        </div>
      </ControlWrapper>

      {/* Farben */}
      <ControlWrapper>
        <div className="flex items-center gap-2 mb-4">
          <Palette className="h-5 w-5 text-muted-foreground" />
          <h3 className="font-semibold">{t("branding.colors")}</h3>
        </div>
        <p className="text-sm text-muted-foreground mb-4">{t("branding.colorsDescription")}</p>
        <div className="space-y-4">
          <ColorInput
            id="branding-primary-color"
            label={t("branding.primaryColor")}
            value={brandingPrimaryColor ?? ""}
            onChange={setBrandingPrimaryColor}
          />
          <ColorInput
            id="branding-cta-color"
            label={t("branding.ctaColor")}
            value={brandingCtaColor ?? ""}
            onChange={setBrandingCtaColor}
          />
          <ColorInput
            id="branding-accent-color"
            label={t("branding.accentColor")}
            value={brandingAccentColor ?? ""}
            onChange={setBrandingAccentColor}
          />
        </div>
      </ControlWrapper>

      {/* Chat-Farben */}
      <ControlWrapper>
        <div className="flex items-center gap-2 mb-4">
          <MessageSquare className="h-5 w-5 text-muted-foreground" />
          <h3 className="font-semibold">{t("branding.chatColors")}</h3>
        </div>
        <p className="text-sm text-muted-foreground mb-4">{t("branding.chatColorsDescription")}</p>
        <div className="space-y-4">
          <ColorInput
            id="branding-user-msg-bg"
            label={t("branding.userMsgBg")}
            value={brandingUserMsgBg ?? ""}
            onChange={setBrandingUserMsgBg}
          />
          <ColorInput
            id="branding-bot-msg-bg"
            label={t("branding.botMsgBg")}
            value={brandingBotMsgBg ?? ""}
            onChange={setBrandingBotMsgBg}
          />
          <ColorInput
            id="branding-user-msg-text"
            label={t("branding.userMsgText")}
            value={brandingUserMsgText ?? ""}
            onChange={setBrandingUserMsgText}
          />
          <ColorInput
            id="branding-bot-msg-text"
            label={t("branding.botMsgText")}
            value={brandingBotMsgText ?? ""}
            onChange={setBrandingBotMsgText}
          />
        </div>
      </ControlWrapper>
    </div>
  );
}
